/**
 * The window and the department, and the reason they live in the URL.
 *
 * Spec 01 §12's acceptance criterion is *"Drill-down giữ nguyên filter context"*.
 * Filter state held in component state satisfies that only by convention — every
 * drill-down has to remember to pass it along, and the one that forgets shows a
 * different population under the same heading. Held in the query string it is
 * satisfied by construction: `attendanceDrillDownPath` reads the same two values
 * every tile does, the back button walks the filter history, and a URL pasted into a
 * message shows the reader the numbers the sender was looking at.
 *
 * ── One clock, and this control is not it ───────────────────────────────────
 *
 * The finish review found two: a radio group reading "7 ngày" under an `<h1>` reading
 * "Hôm nay". The date in the head is the axis (the direction contract's THESIS), so
 * the window is demoted to a **secondary** segmented control whose own label names
 * the day the window ends on — "Xem, kết thúc 14 thg 9" — and the head goes on being
 * the single answer to "which day is this". `?window=` and `?date=` are unchanged;
 * what moved is which of them the eye reads first.
 *
 * ── The department is a picker now ──────────────────────────────────────────
 *
 * It used to be a text box asking for an internal id, under a line promising a list
 * "sẽ có sau". `GET /employees/departments` has been mounted since Task T2-A and
 * `PeopleScreen` has been selecting from it since T2-E-web, so this bar selects from
 * it too — `components/DepartmentSelect.tsx`, shared with `/attendance` and
 * `/devices`. The id is still a **filter**, not a scope: the server intersects it
 * with `WpScope.employeeIds`, so a department outside the caller's field of view
 * narrows the answer or is refused, and never widens it.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { OVERVIEW_WINDOWS, type OverviewWindow } from '../../overview/fold';
import type { MessageKey } from '../../i18n/messages';
import { DepartmentSelect } from '../../components/DepartmentSelect';
import { formatDay } from './DaySheetHead';

const WINDOW_LABEL: Readonly<Record<OverviewWindow, MessageKey>> = {
  today: 'overview.window.today',
  '7d': 'overview.window.7d',
  '30d': 'overview.window.30d',
};

export interface FilterBarProps {
  readonly window: OverviewWindow;
  /** The day the sheet is on — the window ends here, and the label says so. */
  readonly date: string;
  readonly departmentId: string | null;
  readonly onWindow: (next: OverviewWindow) => void;
  readonly onDepartment: (next: string | null) => void;
  readonly onRefresh: () => void;
}

export function FilterBar({
  window,
  date,
  departmentId,
  onWindow,
  onDepartment,
  onRefresh,
}: FilterBarProps): ReactNode {
  const { t, locale } = useI18n();
  const endsOn = formatDay(date, locale, { day: 'numeric', month: 'short' });
  const windowLabel = t('overview.window.label', { date: endsOn });

  return (
    <div
      className="wp-filters wp-daysheet__filters"
      data-testid="k4-filters"
      role="group"
      aria-label={t('overview.filters.label')}
    >
      {/* Secondary, and sized like one: 34px segments beside a 44px picker. The
          three windows are one control rather than three decisions. */}
      <div className="wp-filters__group wp-daysheet__window">
        <span className="wp-filters__legend" id="k4-window-label">
          {windowLabel}
        </span>
        <div className="wp-toggle-group" role="group" aria-labelledby="k4-window-label">
          {OVERVIEW_WINDOWS.map((value) => (
            <button
              key={value}
              type="button"
              className="wp-toggle"
              aria-pressed={window === value}
              onClick={() => {
                onWindow(value);
              }}
              data-testid={`k4-window-${value}`}
            >
              {t(WINDOW_LABEL[value])}
            </button>
          ))}
        </div>
      </div>

      <DepartmentSelect
        id="k4-department"
        testId="k4-department"
        label={t('overview.department.label')}
        value={departmentId}
        onChange={onDepartment}
      />

      <div className="wp-filters__actions">
        <button
          type="button"
          className="wp-button wp-button--sm wp-button--quiet"
          data-testid="k4-refresh"
          onClick={onRefresh}
        >
          {t('overview.refresh')}
        </button>
      </div>
    </div>
  );
}
